/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { bindQuerySelector, type ElementProps, jsx, setClassToken } from '@amazon/vinyl-tsx'
import { createDisposer, debounce } from '@amazon/vinyl-util'
import { onNavigate } from '@/router/router'
import { getAuth, notSignedIn, signedIn } from '@/auth/authRef'
import { Icon } from '@/components/Icon'
import {
    clearSearch,
    getSearchMode,
    showSearch as showSearchRoute,
    updateSearchQuery,
} from '@/search/Search'
import { isMobile, isTv } from '@/device/deviceConfig'
import { onKey } from '@/device/input/KeyMap'
import { Link } from '@/components/Link'
import { CastingButton } from '@/casting/CastingButton'
import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import { getUserInfo, pickProfileImageUrl } from '@/customer/service/userInfoService'
import { getString } from '@/header/i18n/bundle.generated'

export type HeaderProps = ElementProps<'nav'> & {
    readonly headerMenuVisible: MutableValue<boolean>
    readonly deps: HeaderDeps
}

export type HeaderDeps = {
    readonly goodNetwork: ObservableValue<boolean>
}

export function Header(props: HeaderProps) {
    const { headerMenuVisible, deps, ...rest } = props
    // Hide the Upgrade pill once we know the user is on UNLIMITED. Default is
    // hidden so we don't briefly flash the pill for unlimited subscribers
    // before tier info loads.
    const showUpgrade: MutableValue<boolean> = data(false)
    const signInBtn = isTv() ? null : (
        <button
            className="signInBtn textButton"
            onclick={() => {
                getAuth().signIn()
            }}
            visible={notSignedIn()}
        >
            {getString('dmusic_signin_button_text')}
        </button>
    )

    const header = (
        <nav
            {...rest}
            onConnect={(view) => {
                return getSearchMode().onData((searchMode) => {
                    setClassToken(view, 'searchMode', searchMode)
                })
            }}
            visible={
                // Do not show the header on TVs when not signed in.
                signedIn().map((value) => !isTv() || value)
            }
        >
            <div className="headerContents">
                <Link
                    href="/"
                    title="Amazon Music"
                    className="button logoBtn"
                    navigateOptions={{ scroll: 'restore' }}
                >
                    <div className="logoLink"></div>
                </Link>
                <Link
                    className="homeBtn button"
                    title={getString('dmusic_layout_home')}
                    href="/"
                    onclick={clearSearch}
                    navigateOptions={{ scroll: 'restore' }}
                >
                    {/*
                      Render both glyphs and let the .selected toggle on the
                      Link choose which one is visible (filled when active,
                      outline otherwise). Saves the JS swap and keeps the
                      aria-current state authoritative.
                    */}
                    <Icon className="iconSelected" icon="homeFilled" />
                    <Icon className="iconUnselected" icon="homeOutline" />
                    <span className="label">{getString('dmusic_layout_home')}</span>
                </Link>
                {isMobile() ? null : (
                    <Link
                        className="musicBtn button"
                        title={getString('dmusic_browse_home_explore_music_button_label')}
                        href="/music"
                        onclick={clearSearch}
                    >
                        <Icon icon="myMusic" />
                        <span className="label">
                            {getString('dmusic_browse_home_explore_music_button_label')}
                        </span>
                    </Link>
                )}
                {isMobile() ? null : (
                    <Link
                        className="podcastsBtn button"
                        title={getString('dmusic_library_podcasts_pill_title')}
                        href="/podcasts"
                        onclick={clearSearch}
                    >
                        <Icon icon="podcasts" />
                        <span className="label">
                            {getString('dmusic_library_podcasts_pill_title')}
                        </span>
                    </Link>
                )}
                {isMobile() ? null : (
                    <Link
                        className="audibleBtn button"
                        title={getString('dmusic_browse_home_explore_audiobooks_button_label')}
                        href="/audiobooks"
                        onclick={clearSearch}
                        aria-label={getString('dmusic_browse_home_explore_audiobooks_button_label')}
                    >
                        {/*
                          Audible slot has two glyphs — the full brand
                          wordmark for wide viewports and a mark-only
                          fallback that takes over at the same 1280px
                          breakpoint that collapses the rest of the
                          nav-rail labels. CSS toggles which one is
                          visible. Label is reserved for screen
                          readers via aria-label.
                        */}
                        <Icon className="audibleWordmark" icon="audibleWordmark" />
                        <Icon className="audibleMark" icon="audibleLight" />
                    </Link>
                )}
                <Link
                    className="libraryBtn button"
                    title={getString('dmusic_layout_library')}
                    href="/my/library"
                    onclick={clearSearch}
                >
                    <Icon icon="library" />
                    <span className="label">{getString('dmusic_layout_library')}</span>
                </Link>
                <HeaderSearch />
                {signInBtn}

                <div className="sideControls">
                    <button className="upgradeBtn textButton" visible={showUpgrade}>
                        {getString('dmusic_upsell_button_upgrade_text')}
                    </button>
                    <CastingButton deps={deps} />
                    <ProfileAvatarButton
                        headerMenuVisible={headerMenuVisible}
                        showUpgrade={showUpgrade}
                    />
                </div>
            </div>
            <NoConnectivityBanner deps={deps} />
        </nav>
    )

    const viewEl = bindQuerySelector(header)

    const homeBtn = viewEl('.homeBtn')
    const libraryBtn = viewEl('.libraryBtn')
    // Music + Podcasts + Audible are hidden on mobile, so the
    // elements may not exist when the header renders on a phone.
    const musicBtn = header.querySelector<HTMLElement>('.musicBtn')
    const podcastsBtn = header.querySelector<HTMLElement>('.podcastsBtn')
    const audibleBtn = header.querySelector<HTMLElement>('.audibleBtn')
    const searchModeBtn = viewEl('.searchModeBtn')

    function setSelected(el: HTMLElement, selected: boolean) {
        setClassToken(el, 'selected', selected)
        // aria-current marks the active nav item for screen readers; using
        // 'page' since each nav button targets a top-level page.
        if (selected) el.setAttribute('aria-current', 'page')
        else el.removeAttribute('aria-current')
    }

    onNavigate((event) => {
        const path = event.current
        const onHome = !path || path === '/'
        setSelected(homeBtn, onHome)
        setSelected(libraryBtn, path.startsWith('/my/library') || path.startsWith('/library'))
        if (musicBtn) setSelected(musicBtn, path.startsWith('/music'))
        if (podcastsBtn) setSelected(podcastsBtn, path.startsWith('/podcasts'))
        if (audibleBtn) setSelected(audibleBtn, path.startsWith('/audiobooks'))
        setSelected(searchModeBtn, path.startsWith('/search'))
    })

    return header
}

function HeaderSearch() {
    const view = (
        <div className="searchContainer" onConnect={() => onKey('escape', hideSearch)}>
            <button className="searchModeBtn button" onclick={showSearch} tabIndex={0}>
                <Icon icon="search" />
                <span className="label">
                    {getString('dmusic_amazon_music_search_placeholder_text')}
                </span>
            </button>
            <form
                action="#"
                autocomplete="off"
                className="searchForm"
                onsubmit={(event: Event) => {
                    event.preventDefault()
                    search()
                }}
            >
                <input
                    className="searchInput"
                    placeholder={getString('dmusic_amazon_music_search_placeholder_text')}
                    type="search"
                    value=""
                    oninput={debounce(search, 0.6)}
                    onclick={() => {
                        // Pointer clicks into the input from an off-route
                        // page route to /search. Focus alone doesn't —
                        // tab-through and programmatic focus (e.g. iOS
                        // Siri hydration) shouldn't hijack navigation.
                        // Submit (Enter) is handled by the form's
                        // onsubmit, which already routes via search().
                        if (!window.location.pathname.startsWith('/search')) {
                            showSearchRoute(searchInput.value.trim() || '')
                        }
                    }}
                    onConnect={(view) => {
                        const { add, dispose } = createDisposer()
                        const input = view as HTMLInputElement
                        add(
                            onNavigate(() => {
                                input.value = ''
                            })
                        )

                        add(
                            getSearchMode().onData((searchMode) => {
                                if (searchMode) {
                                    // Focus synchronously so the call stays
                                    // inside the user-gesture window iOS
                                    // requires to open the on-screen keyboard.
                                    view.focus()
                                } else {
                                    view.blur()
                                    input.value = ''
                                }
                            })
                        )

                        return dispose
                    }}
                />
                <button className="searchBtn" tabIndex={-1}>
                    <Icon icon="search" />
                </button>
            </form>
        </div>
    )

    const viewEl = bindQuerySelector(view)
    const searchInput = viewEl('.searchInput') as HTMLInputElement

    function search() {
        const query = searchInput.value.trim()
        if (!window.location.pathname.startsWith('/search')) {
            // First keystroke after focus — push /search so the back
            // button returns to where the user came from.
            showSearchRoute(query)
        } else {
            // Already on /search — replace the URL in place so the back
            // button skips over every keystroke instead of unwinding a
            // separate history entry per character.
            updateSearchQuery(query)
        }
    }

    function showSearch() {
        // Navigate to /search and pre-focus the input. Focus must stay
        // synchronous so iOS Safari opens the on-screen keyboard inside
        // the same user gesture.
        showSearchRoute(searchInput.value.trim() || '')
        searchInput.focus()
    }

    function hideSearch() {
        clearSearch()
    }

    return view
}

function ProfileAvatarButton(props: {
    headerMenuVisible: MutableValue<boolean>
    showUpgrade: MutableValue<boolean>
}) {
    const imageUrl: MutableValue<string | null> = data<string | null>(null)

    const view = (
        <button
            className="profileAvatar button"
            onclick={(event) => {
                event.stopImmediatePropagation()
                props.headerMenuVisible.value = !props.headerMenuVisible.value
            }}
            visible={signedIn()}
            aria-label="Profile menu"
            onConnect={() => {
                // Subscribe to the shared user-info observable so the
                // avatar reflects edits (avatar/name) immediately,
                // including overlay-driven optimistic updates from the
                // profile page.
                const signedInSub = signedIn().onData((isIn) => {
                    if (!isIn) {
                        imageUrl.value = null
                        props.showUpgrade.value = false
                    }
                })
                const userInfoSub = getUserInfo().onData((info) => {
                    if (!signedIn().value || !info) {
                        imageUrl.value = null
                        props.showUpgrade.value = false
                        return
                    }
                    imageUrl.value = pickProfileImageUrl(info)
                    // Hide upgrade for unlimited subscribers; show for
                    // FREE / PRIME / unknown so non-unlimited users see
                    // the prompt.
                    props.showUpgrade.value = info.user?.tier !== 'UNLIMITED'
                })
                const imageSub = imageUrl.onData((url) => {
                    view.style.backgroundImage = url ? `url("${url}")` : ''
                    view.classList.toggle('hasImage', !!url)
                })
                return () => {
                    signedInSub()
                    userInfoSub()
                    imageSub()
                }
            }}
        />
    )
    return view
}

function NoConnectivityBanner(props: { deps: HeaderDeps }) {
    const goodNetwork = props.deps.goodNetwork
    return (
        <div
            className="noConnectivityBanner notificationRibbon warning"
            visible={goodNetwork.map((v) => !v)}
        >
            <span className="bannerText">Your connection has limited or no connectivity.</span>
        </div>
    )
}
